interface Highlight {
  value: number;
  label?: string;
  color?: 'coral' | 'violet' | 'sky' | 'sun';
}

interface Props {
  min?: number;
  max?: number;
  marks?: number[];   // specific tick values; falls back to integers min..max
  highlights?: Highlight[];
  title?: string;
}

const colorMap = {
  coral: '#ff7a6b',
  violet: '#b693f6',
  sky: '#2b8bd8',
  sun: '#ffd166',
} as const;

// Number line visualization — integers, fractions on a line, absolute value
// distance, inequalities. Covers sections 1.x, 2.x, 6.1–6.4, 7.5.
export default function NumberLineViz({
  min = -10,
  max = 10,
  marks,
  highlights = [],
  title = 'Number line',
}: Props) {
  const range = max - min;
  if (range <= 0) return null;

  const svgWidth = 440;
  const padding = 24;
  const innerWidth = svgWidth - padding * 2;

  // Convert a math value to an SVG x-coordinate.
  const xOf = (v: number) => padding + ((v - min) / range) * innerWidth;

  const tickValues = marks ?? integerMarks(min, max);

  return (
    <div className="card p-5 space-y-3">
      <h3 className="font-display text-lg">{title}</h3>
      <div className="flex justify-center overflow-x-auto">
        <svg width="100%" viewBox={`0 0 ${svgWidth} 90`} style={{ maxWidth: svgWidth }}>
          {/* Main line with arrowheads */}
          <line
            x1={padding}
            y1={45}
            x2={svgWidth - padding}
            y2={45}
            stroke="#1b2a3a"
            strokeWidth="2"
          />
          <polygon
            points={`${padding},45 ${padding + 10},41 ${padding + 10},49`}
            fill="#1b2a3a"
          />
          <polygon
            points={`${svgWidth - padding},45 ${svgWidth - padding - 10},41 ${svgWidth - padding - 10},49`}
            fill="#1b2a3a"
          />

          {/* Tick marks + labels */}
          {tickValues.map((v, i) => {
            const x = xOf(v);
            return (
              <g key={i}>
                <line x1={x} y1={39} x2={x} y2={51} stroke="#1b2a3a" strokeWidth="1" />
                <text
                  x={x}
                  y={66}
                  textAnchor="middle"
                  fontSize="11"
                  fontFamily="JetBrains Mono, monospace"
                  fill="#6b7582"
                >
                  {v}
                </text>
              </g>
            );
          })}

          {/* Highlight markers — drawn on top of the line */}
          {highlights.map((h, i) => {
            const x = xOf(h.value);
            const fill = colorMap[h.color ?? 'coral'];
            return (
              <g key={`hi-${i}`}>
                <circle cx={x} cy={45} r={8} fill={fill} />
                {h.label && (
                  <text
                    x={x}
                    y={28}
                    textAnchor="middle"
                    fontSize="12"
                    fontWeight="700"
                    fill={fill}
                  >
                    {h.label}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

function integerMarks(min: number, max: number): number[] {
  const span = max - min;
  // Choose a step so we don't crowd the line with labels.
  const step = span <= 10 ? 1 : span <= 20 ? 2 : span <= 50 ? 5 : 10;
  const out: number[] = [];
  // Anchor to multiples of step, starting at or above min.
  const start = Math.ceil(min / step) * step;
  for (let v = start; v <= max; v += step) out.push(v);
  return out;
}
